import {useCallback, useMemo, useReducer} from 'react';

import {Handler, HandlerOf, PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DataFieldsTableUpdater} from '../../../../components/dataTable/useDataFieldsTableActions';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {DataFieldsTableModel} from '../../../../models/api/dataFieldsTableModel';
import {PartialRuleModelStrict} from '../../../../models/api/ruleModel';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {CustomFields} from '../../../editors/commons/customFieldsModel';
import {EditorErrorsEnum} from '../../../editors/commons/editorErrors';
import {ConditionsStateActions, useConditionsState} from '../../../editors/conditions/conditionsState';
import {
  ExpressionDependencies,
  ExpressionStateActions,
  useExpressionState,
} from '../../../editors/expressions/expressionState';
import {parseRuleDryRunError} from '../../../editors/ruleEditor/ruleEditorDryRunErrors';
import {RuleRecipe} from '../../library/recipes';
import {ClientRuleAction} from '../../ruleActionModel';
import {RuleIntegration} from '../../ruleIntegrationModel';
import {TriggerWithConditions} from '../../ruleTriggerHelpers';
import {RuleTriggersTypesEnum} from '../../ruleTriggerModel';
import {RuleEditorActionTypesEnum} from '../state/ruleEditorActions';
import {ruleEditorReducer} from '../state/ruleEditorReducer';
import {removeInvalidExpressionReferences} from '../state/ruleEditorReducerHelpers';
import {RuleEditorState} from '../state/ruleEditorState';

/*
 * Types.
 */

export interface EditorStateActions extends ExpressionStateActions, ConditionsStateActions {
  onUpdateRuleName: HandlerOf<string>;
  onUpdateExampleResourceId: HandlerOf<string | undefined>;

  onToggleIsActive: HandlerOf<boolean>;
  onToggleStopProcessing: Handler;
  onValidation: (key: string, error?: EditorErrorsEnum) => void;
  onSetHiddenErrors: HandlerOf<ReadonlyArray<string>>;

  onAddTrigger: HandlerOf<RuleTriggersTypesEnum>;
  onUpdateTrigger: HandlerOf<TriggerWithConditions>;
  onDeleteTrigger: HandlerOf<RuleTriggersTypesEnum>;

  onAddAction: HandlerOf<ClientRuleAction>;
  onUpdateAction: (
    predicate: PredicateOf<ClientRuleAction>,
    update: (action: ClientRuleAction) => ClientRuleAction,
  ) => void;
  onDeleteAction: (predicate: PredicateOf<ClientRuleAction>) => void;
  onUpdateTable: {
    (value: DataFieldsTableModel): void;
    (setTable: DataFieldsTableUpdater): void;
  };

  onExecDryRun: Handler;
}

/*
 * Custom hook.
 */

export function useEditorState(
  initialRule: PartialRuleModelStrict | undefined,
  customFields: CustomFields,
  ruleIntegration: RuleIntegration,
  initialDataTable: DataFieldsTableModel | undefined,
  namespace?: string,
  recipe?: RuleRecipe,
  hiddenErrors?: ReadonlyArray<string>,
): {ruleEditorState: RuleEditorState} & {
  expressionsDependencies: ExpressionDependencies;
} & EditorStateActions {
  const companyUrl = useAppSelector(getCompanyUrl);

  const [ruleEditorState, dispatch] = useReducer(
    ruleEditorReducer,
    RuleEditorState.createInitialState(
      initialRule,
      customFields,
      initialDataTable,
      namespace,
      recipe,
      hiddenErrors,
    ),
  );

  const onUpdateRuleName = (name: string) => {
    dispatch({type: RuleEditorActionTypesEnum.UPDATE_RULE_NAME, name});
  };

  const onUpdateExampleResourceId = (resourceId: string | undefined) => {
    dispatch({type: RuleEditorActionTypesEnum.UPDATE_EXAMPLE_RESOURCE_ID, resourceId});
  };

  const onToggleIsActive = () => {
    dispatch({type: RuleEditorActionTypesEnum.TOGGLE_IS_ACTIVE});
  };

  const onToggleStopProcessing = () => {
    dispatch({type: RuleEditorActionTypesEnum.TOGGLE_STOP_PROCESSING});
  };

  const onValidation = (key: string, error?: EditorErrorsEnum) => {
    dispatch({type: RuleEditorActionTypesEnum.TOGGLE_ERROR, key, error});
  };

  const onSetHiddenErrors = (cards: ReadonlyArray<string>) => {
    dispatch({type: RuleEditorActionTypesEnum.SET_HIDDEN_ERRORS, cards});
  };

  // TRIGGERS.
  const onAddTrigger = (trigger: RuleTriggersTypesEnum) => {
    dispatch({type: RuleEditorActionTypesEnum.ADD_TRIGGER, trigger, ruleIntegration});
  };
  const onUpdateTrigger = (triggerWithConditions: TriggerWithConditions) => {
    dispatch({type: RuleEditorActionTypesEnum.UPDATE_TRIGGER, triggerWithConditions});
  };
  const onDeleteTrigger = (trigger: RuleTriggersTypesEnum) => {
    dispatch({type: RuleEditorActionTypesEnum.DELETE_TRIGGER, trigger});
  };

  // CONDITIONS.
  const conditionsState = useConditionsState(dispatch);

  // ACTIONS.
  const onAddAction = (condition: ClientRuleAction) => {
    dispatch({type: RuleEditorActionTypesEnum.ADD_ACTION, condition});
  };

  const onUpdateAction = (
    predicate: PredicateOf<ClientRuleAction>,
    update: (action: ClientRuleAction) => ClientRuleAction,
  ) => {
    dispatch({type: RuleEditorActionTypesEnum.UPDATE_ACTION, predicate, update});
  };

  const onDeleteAction = (predicate: PredicateOf<ClientRuleAction>) => {
    dispatch({type: RuleEditorActionTypesEnum.DELETE_ACTION, predicate});
  };

  const onUpdateTable = useCallback((value: DataFieldsTableModel | DataFieldsTableUpdater) => {
    if (typeof value === 'function') {
      return dispatch({type: RuleEditorActionTypesEnum.UPDATE_TABLE, updater: value});
    }
    return dispatch({type: RuleEditorActionTypesEnum.SET_TABLE, table: value});
  }, []);

  // EXPRESSIONS.
  const expressionState = useExpressionState(
    {
      onAddExpressionValidator: (draft) => removeInvalidExpressionReferences(draft, ruleIntegration),
      makeOnDeleteExpressionValidator: (expressionId) => (draft) =>
        removeInvalidExpressionReferences(draft, ruleIntegration, {
          expressionId,
          forceRemoveExpressionReferences: true,
        }),
      makeOnAddAtomValidator: (expressionId) => (draft) =>
        removeInvalidExpressionReferences(draft, ruleIntegration, {expressionId}),
      makeOnUpdateAtomValidator: (expressionId) => (draft) =>
        removeInvalidExpressionReferences(draft, ruleIntegration, {expressionId}),
      makeOnDeleteAtomValidator: (expressionId) => (draft) =>
        removeInvalidExpressionReferences(draft, ruleIntegration, {expressionId}),
    },
    ruleEditorState,
    dispatch,
  );

  // DRY RUN.
  const onExecDryRun = async () => {
    const dryRunEndpoint = `${companyUrl}/namespaces/${namespace}/rules/dryrun`;
    const {exampleResourceId, triggers} = ruleEditorState.ruleState;

    expressionState.onClearDryRun();

    if (!exampleResourceId) {
      return;
    }

    try {
      await expressionState.onUpdateDryRun(dryRunEndpoint, {
        public_id: exampleResourceId,
        events: triggers.map((t) => t.trigger),
      });
    } catch (err: any) {
      expressionState.onDryRunError(parseRuleDryRunError(err));
    }
  };

  const expressionsDependencies = useMemo(
    () => ({
      dataTable: ruleEditorState.dataTable,
    }),
    [ruleEditorState.dataTable],
  );

  return {
    ruleEditorState,
    expressionsDependencies,

    onUpdateRuleName,
    onUpdateExampleResourceId,
    onToggleIsActive,
    onToggleStopProcessing,
    onValidation,
    onSetHiddenErrors,

    onAddTrigger,
    onUpdateTrigger,
    onDeleteTrigger,

    ...conditionsState,

    onAddAction,
    onUpdateAction,
    onDeleteAction,

    onExecDryRun,

    onUpdateTable,

    ...expressionState,
  };
}
